import { useState } from 'react';
import { Card } from '@clay-run/glaze/card';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Text } from '@clay-run/glaze/text';
import { Button } from '@clay-run/glaze/button';
import {
  AlertDialog,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogActions,
} from '@clay-run/glaze/alert-dialog';
import { Trash } from '@phosphor-icons/react/dist/icons/Trash';
import { SCAN_STATUS_BADGE, SCAN_STATUS_LABEL, scanTitle, scanSourceLine, type Scan } from '../scan';

/**
 * The shared scan-row renderer (R5, U4) — prototype's `scanRow()` being one
 * definition shared by every surface that lists a scan
 * (docs/lead-scanner-prototype.html ~line 1193). Originally shared by Home's
 * Draft/Recent lists and the admin-only `AllSubmissions.tsx`; both of those
 * were replaced by the unified Scans page (plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md's `ScansPage.tsx`).
 * `Home.tsx`'s "Scans" card also renders this row now, via the
 * `ScanListCard.tsx` it shares with `ScansPage.tsx` (QA-fix batch, Fix 1).
 */

interface ScanRowProps {
  scan: Scan;
  onOpen: (id: string) => void;
  selected?: boolean;
  onDelete?: (id: string) => void;
  /** The Scans page passes this whenever the effective submitter filter
   *  isn't the viewer — shows which rep scanned this lead. */
  showScannedBy?: boolean;
  /** The viewer's own email. When set and different from `scan.scannedByEmail`,
   *  the delete-confirm dialog names the scan's creator as a friction/
   *  transparency step — deletion itself is never blocked (any authenticated
   *  rep may delete any scan). */
  currentUserEmail?: string | null;
}

export function ScanRow({ scan, onOpen, selected, onDelete, showScannedBy, currentUserEmail }: ScanRowProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const title = scanTitle(scan);

  return (
    <Card padded color={selected ? 'blue' : 'default'} className="flex items-center gap-2">
      {/* Plain <button>, not Card's own onClick — Card's onClick renders the
          whole card as a <button>, and nesting the delete IconButton (also a
          real <button>) inside that would be invalid, broken-parsing HTML.
          Two sibling buttons inside a non-interactive Card is valid. */}
      <button type="button" onClick={() => onOpen(scan.id)} className="flex-1 min-w-0 text-left">
        <Text size="sm" weight="semibold" className="truncate">
          {title}
        </Text>
        <Text size="xs" variant="tertiary" className="truncate">
          {scan.company || scanSourceLine(scan)}
        </Text>
        <Text size="xs" variant="tertiary" className="truncate opacity-70">
          {scanSourceLine(scan)}
        </Text>
        {showScannedBy && (
          <Text size="xs" variant="tertiary" className="truncate opacity-70">
            Scanned by {scan.scannedByEmail}
          </Text>
        )}
      </button>
      <StatusBadge status={SCAN_STATUS_BADGE[scan.status]} size="xs">
        {SCAN_STATUS_LABEL[scan.status]}
      </StatusBadge>
      {onDelete && (
        <>
          <IconButton
            icon={Trash}
            aria-label={`Delete draft — ${title}`}
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmOpen(true);
            }}
          />
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            {/* No `onClose` (round-2 live-QA bug 5): Glaze's AlertDialogHeader
                only renders a focusable X close button when `onClose` is
                passed, and Base UI's dialog moves initial focus to the first
                tabbable element — with the X present, Enter closed this
                destructive confirm instead of doing nothing. Omitting it makes
                Cancel the first tabbable element, so Enter defaults to Cancel
                (ARIA-APG-correct for a destructive confirm) — do not add a
                default-confirm-on-Enter override here. */}
            <AlertDialogHeader>Delete this draft?</AlertDialogHeader>
            <AlertDialogBody>
              <Text size="sm">“{title}” will be removed. This can't be undone.</Text>
              {currentUserEmail && scan.scannedByEmail !== currentUserEmail && (
                <Text size="sm" variant="tertiary">
                  Scanned by {scan.scannedByEmail}.
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogActions>
              <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setConfirmOpen(false);
                  onDelete(scan.id);
                }}
              >
                Delete
              </Button>
            </AlertDialogActions>
          </AlertDialog>
        </>
      )}
    </Card>
  );
}

